import React, { useEffect, useState, useMemo } from 'react';
import api from '../../api/api';
import { showConfirm } from '../../utils/confirmUtils';
import { toastError } from '../../utils/toasts';
import { Card, Button } from '../../components/ui';
import TableToolbar from '../../components/stock/TableToolbar';

const toolbarColumns = [
  { label: 'Nom', accessor: 'name' },
  { label: 'Description', accessor: 'description' },
];

export default function StockCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/stock/categories');
      if (res.data.ok) setCategories(res.data.categories);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const openCreate = () => {
    setEditCat(null);
    setName('');
    setDescription('');
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setName(cat.name);
    setDescription(cat.description || '');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editCat) {
        const res = await api.put(`/stock/categories/${editCat._id}`, { name, description });
        if (res.data.ok) {
          setCategories((prev) => prev.map((c) => (c._id === editCat._id ? res.data.category : c)));
        }
      } else {
        const res = await api.post('/stock/categories', { name, description });
        if (res.data.ok) {
          setCategories((prev) => [...prev, res.data.category]);
        }
      }
      setShowForm(false);
      setEditCat(null);
    } catch (err) {
      console.error('Erreur sauvegarde catégorie:', err);
      toastError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await showConfirm('Supprimer cette catégorie ? Les articles associés ne seront plus catégorisés.')) return;
    try {
      await api.delete(`/stock/categories/${id}`);
      setCategories((prev) => prev.filter((c) => c._id !== id));
    } catch (err) {
      console.error('Erreur suppression catégorie:', err);
      toastError('Erreur lors de la suppression');
    }
  };

  const exportData = useMemo(() => categories.map((c) => ({
    name: c.name,
    description: c.description || '-',
  })), [categories]);

  if (loading) return <p className="text-gray-500 p-6">Chargement...</p>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Catégories d'articles</h1>
          <Button variant="primary" onClick={openCreate}>Nouvelle catégorie</Button>
        </div>

        <TableToolbar
          title="Catégories d'articles"
          columns={toolbarColumns}
          data={exportData}
          filename="categories_stock"
        />

        {showForm && (
          <Card className="mb-6 p-4">
            <h2 className="text-lg font-semibold mb-4">{editCat ? 'Modifier' : 'Nouvelle'} catégorie</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom *</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nom de la catégorie" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full border rounded px-3 py-2 text-sm" placeholder="Description optionnelle" />
              </div>
              <div className="flex gap-2">
                <Button variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Sauvegarde...' : editCat ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button variant="secondary" onClick={() => setShowForm(false)}>Annuler</Button>
              </div>
            </form>
          </Card>
        )}

        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">Nom</th>
                  <th className="text-left px-4 py-2">Description</th>
                  <th className="text-center px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 && (
                  <tr><td colSpan={3} className="text-center py-4 text-gray-500">Aucune catégorie</td></tr>
                )}
                {categories.map((cat) => (
                  <tr key={cat._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{cat.name}</td>
                    <td className="px-4 py-2 text-gray-600">{cat.description || '-'}</td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="secondary" size="sm" onClick={() => openEdit(cat)}>Modifier</Button>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(cat._id)}>Suppr.</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}